import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ClearIcon from '@mui/icons-material/Clear';
import {
  SPATIAL_ZONE_FILTER_OPERATORS,
  SPATIAL_ZONE_OUTPUT_META,
  spatialZoneOutputFields,
} from '../../utils/spatialZoneOutputMeta';

function fieldByFinalName(fields, finalName) {
  return fields.find((f) => f.value === finalName) || null;
}

export default function SpatialZoneResultsTable({
  data = [],
  loading = false,
  error = '',
  spatialZoneAnalysis,
  groupColumns = [],
  onChangeSpatialZoneAnalysis,
  maxPreviewRows = 200,
}) {
  const fields = useMemo(
    () => spatialZoneOutputFields(spatialZoneAnalysis, groupColumns),
    [spatialZoneAnalysis, groupColumns]
  );
  const fieldKeys = useMemo(() => fields.map((f) => f.value), [fields]);

  const resultOps = spatialZoneAnalysis?.resultOps || {};
  const sortField = resultOps.sortField || '';
  const sortDir =
    resultOps.sortDir || SPATIAL_ZONE_OUTPUT_META.defaultSort?.dir || 'desc';
  const filters = Array.isArray(resultOps.filters) ? resultOps.filters : [];
  const limit = resultOps.limit ?? '';

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuField, setMenuField] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [filterOp, setFilterOp] = useState('gte');
  const [filterVal, setFilterVal] = useState('');

  useEffect(() => {
    if (!menuField) return;
    setRenameDraft(spatialZoneAnalysis?.columnLabels?.[menuField.base] || '');
    const existing = filters.find((f) => f.field === menuField.value);
    setFilterOp(existing?.operator || 'gte');
    setFilterVal(existing?.value ?? '');
  }, [menuField, spatialZoneAnalysis?.columnLabels, filters]);

  const patchSpatial = (patch) => {
    onChangeSpatialZoneAnalysis?.({ ...(spatialZoneAnalysis || {}), ...patch });
  };

  const patchResultOps = (patch) => {
    patchSpatial({ resultOps: { ...resultOps, ...patch } });
  };

  const openMenu = (event, field) => {
    setMenuAnchor(event.currentTarget);
    setMenuField(field);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuField(null);
  };

  const commitRename = () => {
    if (!menuField) return;
    const next = String(renameDraft || '').trim();
    const labels = { ...(spatialZoneAnalysis?.columnLabels || {}) };
    if (next && next !== menuField.base) labels[menuField.base] = next;
    else delete labels[menuField.base];
    patchSpatial({ columnLabels: labels });
  };

  const applySort = (dir) => {
    if (!menuField) return;
    patchResultOps({ sortField: menuField.value, sortDir: dir });
    closeMenu();
  };

  const applyFilter = () => {
    if (!menuField || filterVal === '') return;
    const rest = filters.filter((f) => f.field !== menuField.value);
    patchResultOps({
      filters: [...rest, { field: menuField.value, operator: filterOp, value: filterVal }],
    });
    closeMenu();
  };

  const removeFilter = (fieldFinal) => {
    patchResultOps({ filters: filters.filter((f) => f.field !== fieldFinal) });
  };

  const clearAllFilters = () => patchResultOps({ filters: [] });

  const activeFilterFor = (fieldFinal) => filters.find((f) => f.field === fieldFinal);

  const formatCell = (row, field) => {
    const raw = row[field.value];
    if (raw == null || raw === '') return '';
    if (field.numeric && typeof raw === 'number') {
      return Number.isInteger(raw) ? String(raw) : raw.toFixed(2);
    }
    return String(raw);
  };

  return (
    <Paper
      variant="outlined"
      sx={{ flex: 1, minHeight: 280, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}
    >
      {loading && (
        <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }} />
      )}

      <Box
        sx={{
          px: 1,
          py: 0.75,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Resultado zonas
        </Typography>
        <TextField
          size="small"
          type="number"
          label="Top N"
          placeholder="Todos"
          value={limit}
          onChange={(e) => {
            const v = e.target.value.trim();
            patchResultOps({ limit: v === '' ? undefined : Math.max(1, parseInt(v, 10) || 1) });
          }}
          inputProps={{ min: 1 }}
          sx={{ width: 90 }}
        />
        {filters.map((f) => {
          const fld = fieldByFinalName(fields, f.field);
          const label = fld?.value || f.field;
          return (
            <Chip
              key={`${f.field}-${f.operator}-${f.value}`}
              size="small"
              label={`${label} ${f.operator} ${f.value}`}
              onDelete={() => removeFilter(f.field)}
            />
          );
        })}
        {filters.length > 0 && (
          <Button size="small" startIcon={<ClearIcon fontSize="small" />} onClick={clearAllFilters}>
            Quitar filtros
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="warning" sx={{ m: 1, py: 0.25 }}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {fields.map((f) => {
                const sorted = sortField === f.value;
                const filter = activeFilterFor(f.value);
                return (
                  <TableCell key={f.value} sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 90 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" fontWeight={700} noWrap title={f.value}>
                          {f.value.replace(/_/g, ' ')}
                        </Typography>
                        {sorted && (
                          <Typography variant="caption" color="primary" display="block" lineHeight={1}>
                            {sortDir === 'asc' ? '↑ asc' : '↓ desc'}
                          </Typography>
                        )}
                        {filter && (
                          <Tooltip title={`Filtro: ${filter.operator} ${filter.value}`}>
                            <FilterListIcon sx={{ fontSize: 14, color: 'secondary.main', verticalAlign: 'middle' }} />
                          </Tooltip>
                        )}
                      </Box>
                      <IconButton size="small" onClick={(e) => openMenu(e, f)} aria-label={`Opciones ${f.value}`}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data || []).slice(0, maxPreviewRows).map((row, ri) => (
              <TableRow key={ri} hover>
                {fields.map((f) => (
                  <TableCell key={f.value} sx={{ whiteSpace: 'nowrap' }}>
                    {formatCell(row, f)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!loading && !(data || []).length && (
              <TableRow>
                <TableCell colSpan={Math.max(1, fieldKeys.length)}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Sin zonas detectadas con los parámetros actuales.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(menuAnchor && menuField)}
        anchorEl={menuAnchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {menuField && (
          <Box sx={{ p: 2, width: 280, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography variant="subtitle2">{menuField.value.replace(/_/g, ' ')}</Typography>
            <TextField
              size="small"
              fullWidth
              label="Nombre de columna"
              placeholder={menuField.base}
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitRename();
                }
              }}
            />
            <Divider />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<ArrowUpwardIcon />}
                variant={sortField === menuField.value && sortDir === 'asc' ? 'contained' : 'outlined'}
                onClick={() => applySort('asc')}
              >
                Asc
              </Button>
              <Button
                size="small"
                startIcon={<ArrowDownwardIcon />}
                variant={sortField === menuField.value && sortDir === 'desc' ? 'contained' : 'outlined'}
                onClick={() => applySort('desc')}
              >
                Desc
              </Button>
            </Box>
            <Divider />
            <FormControl size="small" fullWidth>
              <InputLabel>Filtro</InputLabel>
              <Select label="Filtro" value={filterOp} onChange={(e) => setFilterOp(e.target.value)}>
                {SPATIAL_ZONE_FILTER_OPERATORS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              fullWidth
              label="Valor"
              value={filterVal}
              onChange={(e) => setFilterVal(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" onClick={applyFilter} disabled={filterVal === ''}>
                Aplicar filtro
              </Button>
              {activeFilterFor(menuField.value) && (
                <Button size="small" onClick={() => { removeFilter(menuField.value); closeMenu(); }}>
                  Quitar
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Popover>
    </Paper>
  );
}
